import { useIdToken } from "@sans-service-tier/sdk-react";
import { getLoginAdapter } from "@sans-service-tier/sdk-web";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { LoadingScreen } from "../../components/LoadingScreen/LoadingScreen";
import { useAnimatedLoadingMessage } from "../../hooks/useAnimatedLoadingMessage";
import {
  impersonationAdminReturnUrlKey,
  impersonationAdminReturnUrlParam,
  impersonationLoadingMessageKey,
  impersonationReturnToAdminKey,
  sansHomeUrl,
} from "../../util/constants";
import { Sans } from "../../util/sdk";

export function Authorizer(): React.ReactElement | null {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading } = useIdToken();
  const loginAdapter = getLoginAdapter();

  const [baseMessage] = useState(() => {
    const message = sessionStorage.getItem(impersonationLoadingMessageKey);
    if (message) {
      sessionStorage.removeItem(impersonationLoadingMessageKey);
    }
    return message ?? "Loading";
  });

  // Use animated loading message - always show as loading during auth process
  const loadingMessage = useAnimatedLoadingMessage(true, baseMessage);

  useEffect(() => {
    const adminReturnUrlFromLocation = new URLSearchParams(location.search).get(impersonationAdminReturnUrlParam);
    if (adminReturnUrlFromLocation) {
      sessionStorage.setItem(impersonationAdminReturnUrlKey, adminReturnUrlFromLocation);
    }
    // No need to do a token check here.
    // If we end up on /authorize while logged in, without any relevant query params
    // (new set of tokens, assumeToken, ?logout=true) the SDK will not do any token updates.
    // At that point, it will call the processLocation callback and reach the branch
    // that navigates to the state value or application root
    if (!loading) {
      loginAdapter.processLocation({}).then(({ queryArgs }) => {
        const adminReturnUrl = queryArgs?.[impersonationAdminReturnUrlParam];
        if (typeof adminReturnUrl === "string" && adminReturnUrl.length > 0) {
          sessionStorage.setItem(impersonationAdminReturnUrlKey, adminReturnUrl);
        }

        const { state, logout } = queryArgs;
        const { id } = Sans.sdk.core.api.tokens;
        if (id) {
          const shouldReturnToAdmin = sessionStorage.getItem(impersonationReturnToAdminKey);
          const adminReturnUrl = sessionStorage.getItem(impersonationAdminReturnUrlKey);

          if (shouldReturnToAdmin && adminReturnUrl) {
            sessionStorage.removeItem(impersonationReturnToAdminKey);
            sessionStorage.removeItem(impersonationAdminReturnUrlKey);
            window.location.assign(adminReturnUrl);
            return;
          }

          navigate((state as string) || "/");
        } else if (logout) {
          // Clear user-specific data from browser when logging out, to suit shared computer use

          // Note that we don't clear some data:
          // * Player settings (volume, muted state, autoplay, captions, playback speed, quality)
          // * UI preferences (coursebook font size, course tab, sidebar width, theme)

          // We do however clear the following:
          localStorage.removeItem("PLAYER_PROGRESS");
          localStorage.removeItem("NETWORK_SPEED");
          localStorage.removeItem("DISMISSED_MESSAGES");
          localStorage.removeItem("dropboxToken");
          localStorage.removeItem("zopimTags");
          localStorage.removeItem("zopimSchedule");
          localStorage.removeItem("zopim_schedule_update");

          // Clear session storage to remove any temporary user data
          sessionStorage.clear();

          window.open(sansHomeUrl, "_self");
        } else {
          loginAdapter.login({ state: location.state?.redirectPath });
        }
      });
    }
  }, [navigate, location.search, location.state, loading, loginAdapter]);

  return <LoadingScreen message={loadingMessage} />;
}
